import { Alert, AlertDescription } from "@nimit9/signet-ui/components/alert"
import { Button } from "@nimit9/signet-ui/components/button"
import {
  extractSettlementCardInfo,
  isSettlementDescription,
  reconcile,
  settlementEnrichedData,
} from "@paisa/reconciliation"
import type { ExistingTransaction, ParsedStatement, ReconcileResult } from "@paisa/types"
import { useEffect, useState } from "react"
import { api } from "@/lib/api"
import { encryptFieldMaybeNoop } from "@/lib/crypto"
import { useCryptoStore } from "@/store/crypto"
import { ReviewBucket } from "./review-bucket"
import { StepSuccess } from "./step-success"
import { TxnRow } from "./txn-row"
import type { ReviewPhase } from "./types"

interface StepReviewProps {
  parsed: ParsedStatement
  accountId: string
  accountType: string
  onBack: () => void
  onDone: () => void
  onImportAnother: () => void
  /** The rows went in (`imported` = how many the API took). */
  onImported?: (imported: number) => void
}

export function StepReview({
  parsed,
  accountId,
  accountType,
  onBack,
  onDone,
  onImportAnother,
  onImported,
}: StepReviewProps) {
  const [phase, setPhase] = useState<ReviewPhase>({ kind: "loading" })

  useEffect(() => {
    async function run() {
      try {
        const dates = parsed.transactions.map((t) => t.date).sort()
        const startDate = parsed.periodStart ?? dates[0]
        const endDate = parsed.periodEnd ?? dates.at(-1)
        if (!startDate || !endDate) throw new Error("Statement has no date range")

        const res = await api.get<{
          success: boolean
          truncated?: boolean
          data: ExistingTransaction[]
        }>(
          `/transactions/reconciliation-context?accountId=${accountId}&startDate=${startDate}&endDate=${endDate}`,
        )
        const result = reconcile(res.data ?? [], parsed)
        setPhase({
          kind: "ready",
          result,
          conflictsToImport: new Set(),
          truncated: res.truncated === true,
        })
      } catch (err) {
        setPhase({ kind: "error", message: err instanceof Error ? err.message : "Failed to load" })
      }
    }
    run()
  }, [accountId, parsed]) // eslint-disable-line react-hooks/exhaustive-deps

  const { key: cryptoKey, mode: cryptoMode } = useCryptoStore()

  async function handleImport(result: ReconcileResult, conflictsToImport: Set<number>) {
    const source = accountType === "credit_card" ? "cc_statement" : "bank_statement"
    const granularity = accountType === "credit_card" ? 2 : 1

    const raw = [
      ...result.unmatched.map((t) => ({ ...t, isDuplicate: false })),
      ...result.conflicts
        .filter((_, i) => conflictsToImport.has(i))
        .map((c) => ({ ...c.incoming, isDuplicate: true })),
    ]

    if (raw.length === 0) {
      onDone()
      return
    }

    setPhase({ kind: "importing" })
    try {
      const toImport = await Promise.all(
        raw.map(async (t) => {
          // Derive settlement-card enrichment from the PLAINTEXT description
          // before encrypting it — the server can't extract it from ciphertext
          // in private mode (#77).
          const enrichedData = settlementEnrichedData(
            t.description,
            t.type as "debit" | "credit",
            source,
          )
          return {
            ...t,
            ...(enrichedData ? { enrichedData } : {}),
            description:
              (await encryptFieldMaybeNoop(t.description, cryptoKey, cryptoMode)) ?? t.description,
            referenceNumber: t.referenceNumber
              ? ((await encryptFieldMaybeNoop(t.referenceNumber, cryptoKey, cryptoMode)) ??
                t.referenceNumber)
              : t.referenceNumber,
          }
        }),
      )
      const res = await api.post<{ success: boolean; imported: number }>(
        "/transactions/import-batch",
        { accountId, source, granularity, transactions: toImport },
      )
      setPhase({ kind: "done", imported: res.imported, parsed, accountId, accountType })
      onImported?.(res.imported)
    } catch (err) {
      setPhase({
        kind: "error",
        message: err instanceof Error ? err.message : "Import failed",
      })
    }
  }

  function toggleConflict(idx: number) {
    setPhase((p) => {
      if (p.kind !== "ready") return p
      const next = new Set(p.conflictsToImport)
      if (next.has(idx)) next.delete(idx)
      else next.add(idx)
      return { ...p, conflictsToImport: next }
    })
  }

  if (phase.kind === "loading") {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-4 px-6 py-10">
        <style>{`
          @keyframes vault-ring-spin {
            from { transform: rotate(-90deg); }
            to   { transform: rotate(270deg); }
          }
        `}</style>
        <svg
          aria-label="Loading"
          width="64"
          height="64"
          viewBox="0 0 64 64"
          style={{
            animation: "vault-ring-spin 1.2s linear infinite",
            transformOrigin: "32px 32px",
          }}
        >
          <circle
            cx="32"
            cy="32"
            r="27"
            fill="none"
            stroke="var(--border-default)"
            strokeWidth="4"
          />
          <circle
            cx="32"
            cy="32"
            r="27"
            fill="none"
            stroke="var(--accent-amber)"
            strokeWidth="4"
            strokeLinecap="round"
            strokeDasharray="120 50"
          />
        </svg>
        <p className="text-[13px] text-text-muted">Checking against existing transactions…</p>
      </div>
    )
  }

  if (phase.kind === "error") {
    return (
      <div className="flex-1 space-y-4 px-6 py-6">
        <Alert variant="destructive">
          <AlertDescription>{phase.message}</AlertDescription>
        </Alert>
        <Button variant="ghost" onClick={onBack}>
          Back
        </Button>
      </div>
    )
  }

  if (phase.kind === "done") {
    return (
      <StepSuccess
        imported={phase.imported}
        parsed={phase.parsed}
        accountId={phase.accountId}
        accountType={phase.accountType}
        onDone={onDone}
        onImportAnother={onImportAnother}
      />
    )
  }

  if (phase.kind === "importing") {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-4 px-6 py-10">
        <div className="h-1 w-full max-w-xs overflow-hidden rounded-full bg-border-default">
          <div
            className="h-full rounded-full"
            style={{
              width: "60%",
              background: "linear-gradient(90deg, var(--accent-amber), #f97316)",
              animation: "vault-progress 1.5s ease-in-out infinite",
            }}
          />
        </div>
        <p className="text-[13px] text-text-muted">Importing…</p>
      </div>
    )
  }

  const { result, conflictsToImport } = phase

  // Detect CC bill payments in the *incoming* unmatched transactions so we can
  // nudge the user to also import their CC statement for a full breakdown.
  const incomingSettlementBrands = [
    ...new Set(
      result.unmatched
        .filter((t) => t.type === "debit" && isSettlementDescription(t.description) !== null)
        .map((t) => extractSettlementCardInfo(t.description).brand)
        .filter((b): b is string => b !== null),
    ),
  ]

  const total =
    result.unmatched.length +
    result.matched.length +
    result.conflicts.length +
    result.settlements.length
  const autoSorted = result.unmatched.length
  const importCount = result.unmatched.length + conflictsToImport.size

  return (
    <>
      <div className="flex-1 overflow-y-auto px-6 py-4">
        <p className="font-mono text-[10.5px] uppercase tracking-widest text-accent-amber mb-1">
          Step 3 of 3
        </p>
        <h3 className="text-[20px] font-bold tracking-tight text-text-primary mb-1">
          Ready in a moment
        </h3>
        {phase.truncated && (
          <Alert className="mb-4">
            <AlertDescription>
              Showing only the first 1,000 existing transactions in range — narrow the statement
              date range, or matches beyond that may be missed.
            </AlertDescription>
          </Alert>
        )}
        <p className="text-[12.5px] text-text-muted mb-4 leading-relaxed">
          We've sorted through everything. Here's what'll happen.
        </p>

        {/* Confidence hero */}
        <div
          className="mb-4 rounded-[14px] border px-[18px] py-4"
          style={{
            background: "linear-gradient(135deg, rgba(52,211,153,0.08), rgba(245,158,11,0.03))",
            borderColor: "rgba(52,211,153,0.2)",
          }}
        >
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="text-[26px] font-bold tracking-tight leading-none text-text-primary">
                {autoSorted} of {total} ready{" "}
                <span className="text-[18px]" style={{ color: "var(--accent-green)" }}>
                  ✓
                </span>
              </p>
              <p className="mt-1.5 text-[11.5px] text-text-muted">
                {total > 0 ? Math.round((autoSorted / total) * 100) : 0}% auto-sorted
                {result.conflicts.length > 0 && ` · ${result.conflicts.length} need your eye`}
                {result.matched.length > 0 && ` · ${result.matched.length} already in Paisa`}
              </p>
            </div>
            {/* Mini bar */}
            <div className="flex flex-col items-end gap-1">
              <div className="flex gap-0.5">
                {total > 0 && (
                  <>
                    <div
                      style={{
                        width: Math.max(4, Math.round((autoSorted / total) * 100)),
                        height: 7,
                        background: "var(--accent-green)",
                        borderRadius: 2,
                      }}
                    />
                    {result.conflicts.length > 0 && (
                      <div
                        style={{
                          width: Math.max(4, Math.round((result.conflicts.length / total) * 100)),
                          height: 7,
                          background: "var(--accent-amber)",
                          borderRadius: 2,
                        }}
                      />
                    )}
                    {result.matched.length + result.settlements.length > 0 && (
                      <div
                        style={{
                          width: Math.max(
                            4,
                            Math.round(
                              ((result.matched.length + result.settlements.length) / total) * 100,
                            ),
                          ),
                          height: 7,
                          background: "var(--text-hint)",
                          borderRadius: 2,
                        }}
                      />
                    )}
                  </>
                )}
              </div>
              <span className="font-mono text-[9px] uppercase tracking-widest text-text-muted">
                confidence
              </span>
            </div>
          </div>
        </div>

        {/* Stat bar */}
        <div
          className="mb-4 grid grid-cols-4 overflow-hidden rounded-[12px] border border-border-subtle"
          style={{ background: "var(--border-subtle)", gap: 1 }}
        >
          {[
            { num: result.unmatched.length, label: "New txns", color: "var(--accent-green)" },
            { num: result.conflicts.length, label: "Possible dupes", color: "var(--accent-amber)" },
            { num: result.matched.length, label: "Already in Paisa", color: "var(--text-muted)" },
            {
              num: result.settlements.length,
              label: "CC settlements",
              color: "var(--accent-amber)",
            },
          ].map(({ num, label, color }) => (
            <div key={label} className="bg-bg-elevated px-3.5 py-3.5">
              <p className="text-[22px] font-bold leading-none tracking-tight" style={{ color }}>
                {num}
              </p>
              <p className="mt-1.5 text-[9.5px] uppercase tracking-widest text-text-muted">
                {label}
              </p>
            </div>
          ))}
        </div>

        {/* Buckets */}
        <ReviewBucket
          label="New transactions"
          sublabel="will be imported"
          count={result.unmatched.length}
          tone="green"
          icon="✓"
          defaultOpen={true}
          emptyText="No new transactions — all already in Paisa."
        >
          {result.unmatched.slice(0, 3).map((t) => (
            <TxnRow
              key={`${t.date}-${t.description}`}
              date={t.date}
              description={t.description}
              amount={t.amount}
              type={t.type}
            />
          ))}
          {result.unmatched.length > 3 && (
            <p className="py-2 text-[11.5px] text-text-muted">
              + {result.unmatched.length - 3} more · all auto-categorised
            </p>
          )}
        </ReviewBucket>

        {result.conflicts.length > 0 && (
          <ReviewBucket
            label="Possible duplicates"
            sublabel="needs your eye"
            count={result.conflicts.length}
            tone="amber"
            icon="!"
            defaultOpen={true}
          >
            {result.conflicts.map((c, i) => (
              <TxnRow
                key={`${c.incoming.date}-${c.incoming.description}`}
                date={c.incoming.date}
                description={c.incoming.description}
                amount={c.incoming.amount}
                type={c.incoming.type}
                note={c.reason}
                checked={conflictsToImport.has(i)}
                onCheck={() => toggleConflict(i)}
              />
            ))}
            <p className="mt-1 text-[11px] text-text-muted">Check any you want to import anyway.</p>
          </ReviewBucket>
        )}

        {result.matched.length > 0 && (
          <ReviewBucket
            label="Already in Paisa"
            sublabel="will be skipped"
            count={result.matched.length}
            tone="muted"
            icon="⟲"
            emptyText="No matches."
          >
            {result.matched.map((m) => (
              <TxnRow
                key={`${m.incoming.date}-${m.incoming.description}`}
                date={m.incoming.date}
                description={m.incoming.description}
                amount={m.incoming.amount}
                type={m.incoming.type}
                note={m.reason}
              />
            ))}
          </ReviewBucket>
        )}

        {result.settlements.length > 0 && (
          <ReviewBucket
            label="CC bill payments"
            sublabel="treated as transfers, skipped"
            count={result.settlements.length}
            tone="brand"
            icon="⇌"
          >
            {result.settlements.map((s) => (
              <TxnRow
                key={`${s.bankDebit.date}-${s.bankDebit.description}`}
                date={s.bankDebit.date}
                description={s.bankDebit.description}
                amount={s.bankDebit.amount}
                type="debit"
                note="Transfer — already recorded"
              />
            ))}
          </ReviewBucket>
        )}
        {incomingSettlementBrands.length > 0 && (
          <div
            className="mt-3 rounded-[12px] border px-4 py-3.5"
            style={{ borderColor: "rgba(245,158,11,0.22)", background: "rgba(245,158,11,0.05)" }}
          >
            <p className="text-[12.5px] font-semibold text-text-primary">
              {incomingSettlementBrands.map((b) => b.toUpperCase()).join(" + ")} bill payment
              detected
            </p>
            <p className="mt-1 text-[11.5px] text-text-muted">
              Import your {incomingSettlementBrands.map((b) => b.toUpperCase()).join("/")} CC
              statement after this to see the full spending breakdown — otherwise the total shows as
              one line.
            </p>
          </div>
        )}
      </div>

      {/* Import CTA */}
      <div>
        <div
          className="mx-6 mb-3 mt-3 flex items-center justify-between gap-3 rounded-[14px] border px-4 py-3.5"
          style={{
            background: "linear-gradient(180deg, rgba(245,158,11,0.12), rgba(245,158,11,0.04))",
            borderColor: "rgba(245,158,11,0.28)",
          }}
        >
          <div>
            <p className="text-[17px] font-bold tracking-tight text-text-primary">
              Import {importCount} transaction{importCount !== 1 ? "s" : ""}
            </p>
            <p className="mt-0.5 text-[11.5px] text-text-muted">
              {result.unmatched.length} new
              {conflictsToImport.size > 0 && ` · ${conflictsToImport.size} duplicate kept`}
              {result.matched.length > 0 && ` · ${result.matched.length} skipped`}
              {result.settlements.length > 0 && ` · ${result.settlements.length} transfers`}
            </p>
          </div>
          <Button
            size="sm"
            disabled={importCount === 0}
            onClick={() => handleImport(result, conflictsToImport)}
            className="shrink-0"
          >
            Import →
          </Button>
        </div>

        <div className="flex items-center justify-between border-t border-border-subtle px-6 py-3">
          <Button variant="ghost" size="sm" onClick={onBack}>
            Back
          </Button>
          <span className="font-mono text-[10px] uppercase tracking-widest text-text-muted">
            Takes about 1 second · all local
          </span>
        </div>
      </div>
    </>
  )
}
